import React, { useEffect, useRef } from 'react'
import { useAppStore } from '../../store/appStore'
import { MobileHistoryButtons } from '../NavigationHistory'
import { LoadingProgressBar } from '../LoadingProgressBar/LoadingProgressBar'

interface MobileControlsOverlayProps {
  onBack: () => void
  onMenuClick: () => void
  isMenuOpen: boolean
  isFullscreen: boolean
  isFullscreenSupported: boolean
  onToggleFullscreen: () => void | Promise<void>
}

/**
 * Unified mobile controls overlay containing back button, hamburger menu, and fullscreen button
 * Auto-hides after 3 seconds of inactivity on mobile
 * Shows on any touch interaction
 */
export function MobileControlsOverlay({
  onBack,
  onMenuClick,
  isMenuOpen,
  isFullscreen,
  isFullscreenSupported,
  onToggleFullscreen,
}: MobileControlsOverlayProps) {
  const isVisible = useAppStore(state => state.controlsVisible)
  const setControlsVisible = useAppStore(state => state.setControlsVisible)
  const showControls = useAppStore(state => state.showControls)
  const hideControls = useAppStore(state => state.hideControls)
  const is3DMode = useAppStore(state => state.is3DMode)
  const setIs3DMode = useAppStore(state => state.setIs3DMode)
  const trajectoryOverlayVisible = useAppStore(state => state.trajectoryOverlayVisible)
  const setTrajectoryOverlayVisible = useAppStore(state => state.setTrajectoryOverlayVisible)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const isMovingRef = useRef<boolean>(false)
  const [isFading, setIsFading] = React.useState(false)
  const isInitializedRef = useRef(false)

  // Debug: Log trajectory visibility changes
  React.useEffect(() => {
    console.log('🔍 [TrajectoryButton] trajectoryOverlayVisible changed:', trajectoryOverlayVisible)
  }, [trajectoryOverlayVisible])

  // Ensure controls are HIDDEN on mount (not visible initially)
  React.useEffect(() => {
    console.log('🎬 [MobileControlsOverlay] Mount: Setting controlsVisible to FALSE (hidden initially)')
    isInitializedRef.current = false
    // Start with controls hidden
    useAppStore.getState().setControlsVisible(false)
    
    // Delay initialization to prevent race conditions
    const initTimer = setTimeout(() => {
      isInitializedRef.current = true
      console.log('🎬 [MobileControlsOverlay] Initialization complete')
    }, 500)
    
    return () => clearTimeout(initTimer)
  }, [])

  // Debug: Log visibility changes
  React.useEffect(() => {
    console.log('👁️ [MobileControlsOverlay] isVisible changed:', isVisible)
  }, [isVisible])

  // Reset auto-hide timer
  const resetAutoHideTimer = () => {
    // CRITICAL: Always clear existing timeouts first
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
    }
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current)
    }
    // Reset fade state when timer resets
    setIsFading(false)
    
    // Don't auto-hide if menu is open
    if (isMenuOpen) {
      return
    }
    
    // Start fade after 8 seconds
    fadeTimeoutRef.current = setTimeout(() => {
      console.log('⏱️ [MobileOverlay] Fading out after 8 seconds')
      setIsFading(true)
    }, 8000)
    
    // Hide after 10 seconds (2 seconds after fade starts)
    hideTimeoutRef.current = setTimeout(() => {
      console.log('⏱️ [MobileOverlay] Hiding controls after 10 seconds')
      useAppStore.getState().hideControls()
    }, 10000)
  }

  // Toggle controls on tap
  const toggleControls = () => {
    // Skip toggling during initialization
    if (!isInitializedRef.current) {
      console.log('🔄 [MobileControlsOverlay] Skipping toggle - still initializing')
      return
    }
    
    const currentVisibility = useAppStore.getState().controlsVisible
    const tapIndicatorActive = document.querySelector('[data-tap-indicator="true"]') !== null
    
    console.log('🔄 [MobileControlsOverlay] toggleControls called, current:', currentVisibility, 'tapIndicatorActive:', tapIndicatorActive)
    
    // If controls are visible and indicator is active, don't hide them
    if (currentVisibility && tapIndicatorActive) {
      console.log('🔄 [MobileControlsOverlay] Skipping toggle - controls visible and tap indicator active')
      return
    }
    
    // Always show controls on tap (regardless of current state)
    // This ensures they become visible when tapping the canvas
    console.log('🔄 [MobileControlsOverlay] Showing controls on tap')
    console.log('🔄 [DEBUG] About to call showControls from store')
    const store = useAppStore.getState()
    console.log('🔄 [DEBUG] Store state before:', store.controlsVisible)
    store.showControls()
    console.log('🔄 [DEBUG] Store state after:', useAppStore.getState().controlsVisible)
    
    // Restart the auto-hide timer
    resetAutoHideTimer()
  }

  // Detect touch interactions anywhere in the viewport
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement
      
      // Ignore touches on buttons or interactive elements
      if (target.closest('button') || target.closest('.navigation-menu') || target.closest('.settings-panel') || target.closest('.sections-drawer') || target.closest('.sections-drawer__backdrop') || target.closest('[data-chat-balloon]')) {
        return
      }
      
      // Record touch start position and time
      if (e.touches && e.touches.length > 0) {
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        }
        isMovingRef.current = false
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      // If any multi-touch or significant movement detected, mark as moving/panning
      if (e.touches && e.touches.length > 1) {
        isMovingRef.current = true
      } else if (e.touches && e.touches.length === 1 && touchStartRef.current) {
        const dx = e.touches[0].clientX - touchStartRef.current.x
        const dy = e.touches[0].clientY - touchStartRef.current.y
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance > 10) {
          isMovingRef.current = true
        }
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement
      
      // Ignore touches on buttons or interactive elements
      // Also ignore touches on SVG elements (trajectory circles, etc.)
      // Also ignore touches on tap camera indicator circle, badge, and overlays
      // Also ignore touches on camera thumbnail list
      if (target.closest('button') || 
          target.closest('.navigation-menu') || 
          target.closest('.settings-panel') || 
          target.closest('.sections-drawer') || 
          target.closest('.sections-drawer__backdrop') || 
          target.closest('svg') || 
          target.closest('[data-tap-indicator]') ||
          target.closest('[data-tap-badge]') ||
          target.closest('[data-tap-circle-overlay]') ||
          target.closest('[data-drag-overlay]') ||
          target.closest('.camera-thumbnail-list') ||
          target.closest('[data-chat-balloon]')) {
        console.log('🔄 [MobileControlsOverlay] TouchEnd ignored - on excluded element')
        touchStartRef.current = null
        isMovingRef.current = false
        return
      }
      
      // If it was a drag/pan, don't process as tap - just restart timer
      if (isMovingRef.current) {
        touchStartRef.current = null
        isMovingRef.current = false
        // Restart timer on any pan activity
        resetAutoHideTimer()
        return
      }
      
      // Only process as tap if it was a genuine tap (no movement detected)
      // Don't toggle controls if menu or settings are open
      if (isMenuOpen) {
        touchStartRef.current = null
        isMovingRef.current = false
        return
      }
      
      // CRITICAL: Prevent this touchend from triggering a click event
      e.preventDefault()
      
      console.log('🔄 [MobileControlsOverlay] TouchEnd - calling toggleControls')
      toggleControls()
      
      touchStartRef.current = null
      isMovingRef.current = false
    }

    // Also add click listener for desktop (single click toggles)
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      
      // Ignore clicks on buttons or interactive elements
      // Also ignore clicks on SVG elements (trajectory circles, etc.)
      // Also ignore clicks on tap camera indicator (circle, badge, etc.)
      // Also ignore clicks on camera thumbnail list
      if (target.closest('button') || 
          target.closest('.navigation-menu') || 
          target.closest('.settings-panel') || 
          target.closest('.sections-drawer') || 
          target.closest('.sections-drawer__backdrop') || 
          target.closest('svg') || 
          target.closest('[data-tap-indicator]') ||
          target.closest('[data-tap-badge]') ||
          target.closest('[data-tap-circle-overlay]') ||
          target.closest('[data-drag-overlay]') ||
          target.closest('.camera-thumbnail-list') ||
          target.closest('[data-chat-balloon]')) {
        console.log('🔄 [MobileControlsOverlay] Click ignored - on excluded element')
        return
      }
      
      console.log('🔄 [MobileControlsOverlay] Click not ignored - calling toggleControls')
      // Toggle on desktop click
      toggleControls()
    }

    // Add listeners to entire document to catch all touch interactions
    // Use capture phase to ensure we catch taps even when controls are hidden
    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true })
    // touchend: NOT passive so we can preventDefault() to block click events
    document.addEventListener('touchend', handleTouchEnd, { passive: false, capture: true })
    document.addEventListener('click', handleClick, { capture: true })

    // Start auto-hide timer when component mounts
    // This will hide controls after 10 seconds of inactivity
    // resetAutoHideTimer() will be called on tap to show and restart the timer
    // (skip timer on initial mount to prevent immediate hide)

    return () => {
      document.removeEventListener('touchstart', handleTouchStart, { capture: true })
      document.removeEventListener('touchmove', handleTouchMove, { capture: true })
      document.removeEventListener('touchend', handleTouchEnd, { capture: true })
      document.removeEventListener('click', handleClick, { capture: true })
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current)
      }
    }
  }, [])

  // Prevent hiding when controls themselves are clicked
  const handleControlsInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
    useAppStore.getState().showControls()
  }

  const handleBackClick = (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation()
    onBack()
  }

  const handleMenuClick = (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation()
    onMenuClick()
  }

  const handleFullscreenClick = async (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation()
    const result = onToggleFullscreen()
    if (result instanceof Promise) {
      await result
    }
  }

  const handle3DToggle = (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation()
    setIs3DMode(!is3DMode)
    useAppStore.getState().showControls()
  }

  const handleTrajectoryToggle = (e: React.TouchEvent | React.MouseEvent) => {
    e.stopPropagation()
    const currentState = useAppStore.getState().trajectoryOverlayVisible
    console.log('🎨 [TrajectoryToggle] Current:', currentState, '→ New:', !currentState)
    useAppStore.getState().setTrajectoryOverlayVisible(!currentState)
  }

  // Get loading state from store
  const loadingProgress = useAppStore(state => state.loadingProgress)
  const loadingStage = useAppStore(state => state.loadingStage)
  const canUseTapIndicator = useAppStore(state => state.canUseTapIndicator)
  const exploreMode = useAppStore(state => state.exploreMode)
  const setExploreMode = useAppStore(state => state.setExploreMode)

  return (
    <>
      {/* PROGRESS BAR - TOP OF SCREEN (visible during Stage 2 loading) */}
      {!canUseTapIndicator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100010,
          pointerEvents: 'none'
        }}>
          <LoadingProgressBar progress={loadingProgress} stage={loadingStage} />
        </div>
      )}

      {/* Back Button - ALWAYS VISIBLE (not affected by auto-hide) */}
      <button
        className="control-button back-button"
        onClick={handleBackClick}
        aria-label="Back"
        title="Go back"
        style={{
          position: 'fixed',
          top: '16px',
          left: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          padding: '0',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.35)',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          backdropFilter: 'blur(8px) saturate(150%)',
          color: 'white',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '600',
          lineHeight: '1',
          zIndex: 100003,
          pointerEvents: 'auto',
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: 'none' }}
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Other Controls - AUTO-HIDE after 10 seconds */}
      <div
        ref={containerRef}
        data-mobile-overlay="true"
        className={`mobile-controls-overlay ${isVisible ? 'visible' : 'hidden'} ${isFading ? 'fading' : ''}`}
        style={{
          position: 'fixed',
          top: '16px',
          left: '76px',
          display: 'flex',
          gap: '12px',
          zIndex: 100002,
          alignItems: 'center',
          justifyContent: 'flex-start',
          pointerEvents: 'auto',
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? 'visible' : 'hidden',
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Hamburger Menu Button */}
      <button
        className={`control-button menu-button ${isMenuOpen ? 'active' : ''}`}
        onClick={handleMenuClick}
        aria-label="Menu"
        title="Open menu"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          padding: '0',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.35)',
          background: isMenuOpen 
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          backdropFilter: 'blur(8px) saturate(150%)',
          color: 'white',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '600',
          lineHeight: '1',
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: 'none' }}
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Fullscreen Button */}
      {isFullscreenSupported && (
        <button
          className="control-button fullscreen-button"
          onClick={handleFullscreenClick}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            padding: '0',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.35)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
            backdropFilter: 'blur(8px) saturate(150%)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '20px',
            fontWeight: '600',
            lineHeight: '1',
          }}
        >
          {isFullscreen ? (
            // Exit fullscreen icon (minimize)
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ pointerEvents: 'none' }}
            >
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
            </svg>
          ) : (
            // Enter fullscreen icon (expand)
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ pointerEvents: 'none' }}
            >
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
          )}
        </button>
      )}

      {/* 3D View Toggle Button */}
      <button
        className={`control-button 3d-button ${is3DMode ? 'active' : ''}`}
        onClick={handle3DToggle}
        aria-label={is3DMode ? 'Exit 3D view' : 'Enter 3D view'}
        title={is3DMode ? 'Exit 3D view' : 'Enter 3D view'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          padding: '0',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.35)',
          background: is3DMode
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          backdropFilter: 'blur(8px) saturate(150%)',
          color: 'white',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '600',
          lineHeight: '1',
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: 'none' }}
        >
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7" />
          <circle cx="12" cy="12" r="3" />
          <path d="M15 3l4-2M9 3l-4-2" />
          <path d="M15 21l4 2M9 21l-4 2" />
        </svg>
      </button>

      {/* Trajectory Overlay Toggle Button */}
      <button
        className={`control-button trajectory-button ${trajectoryOverlayVisible ? 'active' : ''}`}
        onClick={(e) => {
          console.log('🔘 [Button Click] Before toggle, trajectoryOverlayVisible:', trajectoryOverlayVisible)
          handleTrajectoryToggle(e)
        }}
        aria-label={trajectoryOverlayVisible ? 'Hide trajectory overlay' : 'Show trajectory overlay'}
        title={trajectoryOverlayVisible ? 'Hide trajectory overlay' : 'Show trajectory overlay'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          padding: '0',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.35)',
          background: trajectoryOverlayVisible
            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.35), rgba(255, 255, 255, 0.25))'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
          backdropFilter: 'blur(8px) saturate(150%)',
          color: 'white',
          cursor: 'pointer',
          fontSize: '20px',
          fontWeight: '600',
          lineHeight: '1',
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ pointerEvents: 'none' }}
        >
          {trajectoryOverlayVisible ? (
            // Smiling face (active - trajectory visible)
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
          ) : (
            // Neutral/sleepy face (inactive - trajectory hidden)
            <>
              <circle cx="12" cy="12" r="10" />
              <line x1="8" y1="10" x2="10" y2="10" />
              <line x1="14" y1="10" x2="16" y2="10" />
              <line x1="9" y1="16" x2="15" y2="16" />
            </>
          )}
        </svg>
      </button>

      {/* Navigation History Buttons */}
      <MobileHistoryButtons />
    </div>
    </>
  )
}
